import { isEmpty } from "@utils/var.check";

const locale = "en";
const collator = new Intl.Collator(locale);

/**
 * Utilities for working with Strings.
 * @module StringUtils
 */
const StringUtils = {
  isString(value) {
    if (typeof value === "string" || value instanceof String) {
      return true;
    }
    return false;
  },
  getStringCollator() {
    return collator;
  },
  /**
   * @description This method transforms the case of the string to title case
   * @param {String} str
   * @returns {Sring} The transformed string in title case.
   * @examples "ThE QuIcK BrOwn FoX JuMps" -> "The Quick Brown Fox Jumps"
   */
  toTitleCase(str) {
    return str
      .toLowerCase()
      .split(" ")
      .map((word) => {
        if (word.length <= 0) {
          return "";
        }
        return word.replace(word[0], word[0].toUpperCase());
      })
      .join(" ");
  },
  toTitleCaseWithSpecialCharacters(str) {
    return str.replace(/\b\w+('\w)?/g, function (txt) {
      return txt.charAt(0).toUpperCase() + txt.substr(1).toLowerCase();
    });
  },

  /**
   * @description This method strip html tag of the string
   * @param {String} str
   * @returns {Sring} The transformed string without html tag.
   * @examples "<div class="paragraph">Outcomes 1</div>" -> "Outcomes 1"
   */
  toStripHtml(str) {
    return str.replace(/<[^>]*>?/gm, "");
  },
  /**
   *
   * @description Convert integer to roman value
   * @param {Integer} num
   * @returns {String}
   */
  getRomanValue(num) {
    if (isNaN(num)) {
      return "";
    }

    const digits = String(+num).split("");
    const key = [
      "",
      "c",
      "cc",
      "ccc",
      "cd",
      "d",
      "dc",
      "dcc",
      "dccc",
      "cm",
      "",
      "x",
      "xx",
      "xxx",
      "xl",
      "l",
      "lx",
      "lxx",
      "lxxx",
      "xc",
      "",
      "i",
      "ii",
      "iii",
      "iv",
      "v",
      "vi",
      "vii",
      "viii",
      "ix",
    ];
    let roman = "";
    let i = 3;
    while (i > 0) {
      i--;
      roman = (key[+digits.pop() + i * 10] || "") + roman;
    }
    return Array(+digits.join("") + 1).join("M") + roman;
  },
  /**
   * @description Convert number to letter based on alphabetical order. If number is larger than 26, it will continue to enumerate, with Z1, Z2, Z3, etc
   * @param {Number} index
   * @returns {String}
   */
  getAlphabeticalLetter(index) {
    if (typeof index === "string") {
      index = parseInt(index);
    }
    if (isNaN(index)) {
      return "";
    }
    if (typeof index !== "number") {
      return "";
    }
    if (index < 0) {
      return "";
    }
    if (index < ALPHABETS.length) {
      return ALPHABETS[index];
    } else {
      const overflowNumber = index - ALPHABETS.length + 1;
      return ALPHABETS[ALPHABETS.length - 1] + overflowNumber;
    }
  },
  /**
   *
   * @description Convert string with special characters to string with alphanumeric. Special characters would be replaced by replacingChar
   * @param {String} str
   * @param {String} replacingChar
   * @returns {String}
   */
  getAlphanumeric(str, replacingChar) {
    const regex = /(\s|\(|\)|'|-|,|\/|&)/g;
    return !isEmpty(str) ? str.replace(regex, replacingChar) : "";
  },

  /**
   * @description Checks if string contains alphaNumeric or space characters. Reference: https://stackoverflow.com/a/25352300
   * @param {*} str
   * @returns {Boolean}
   */
  isAlphaNumericOrSpace(str) {
    let code, i, len;

    for (i = 0, len = str.length; i < len; i++) {
      code = str.charCodeAt(i);
      if (
        code !== 32 && // space
        !(code > 47 && code < 58) && // numeric (0-9)
        !(code > 64 && code < 91) && // upper alpha (A-Z)
        !(code > 96 && code < 123) // lower alpha (a-z)
      ) {
        return false;
      }
    }
    return true;
  },

  /**
   * @description Returns a formatted number that is abbreviated
   * @param {*} value
   * @returns {String}
   */
  formatNumberWithAbbreviation(value) {
    const thousand = 1000;
    const million = 1000000;
    const billion = 1000000000;

    if (value < thousand) {
      // less than thousand number
      return value;
    } else if (value < million) {
      // thousand number
      return Math.floor(value / thousand) + "K";
    } else if (value < 10 * million) {
      // cater for 1 decimal allow to display (1,000,000 -> 9,999,999)
      return Math.floor(value / 100000) / 10 + "M";
    } else if (value < billion) {
      // million number (> 10,000,000)
      return Math.floor(value / million) + "M";
    } else {
      // billion number (> 1000,000,000)
      return Math.floor(value / billion) + "B";
    }
  },
  /**
   * @description Returns a formatted number that is abbreviated in any number of decimal place
   * @param {int} value
   * @param {int} decimalPlace
   * @returns {String}
   */
  formatNumberToDecimalPlaceWithAbbreviation(value, decimalPlace) {
    const thousand = 1000;
    const million = 1000000;
    const billion = 1000000000;
    let formattedValue;
    if (value < thousand) {
      // less than thousand number
      formattedValue = value.toString();
    } else if (value < million) {
      // thousand number
      formattedValue = (value / thousand).toFixed(decimalPlace) + "K";
    } else if (value < billion) {
      // million number (> 10,000,000)
      formattedValue = (value / million).toFixed(decimalPlace) + "M";
    } else {
      // billion number (> 1000,000,000)
      formattedValue = (value / billion).toFixed(decimalPlace) + "B";
    }
    // remove extra zero for case(eg: "100.0" -> "100")
    return formattedValue.replace(".0", "");
  },
  /**
   * @description Returns a formatted string with space after comma (,)
   * @param {String} str
   * @returns {String}
   */
  formatSpaceAfterComma(str) {
    return str.replace(/\s*,\s*/g, ", ");
  },
  /**
   *
   * @description Compare if two strings in lower case are the same.
   * @param {String} firstStr
   * @param {String} secondStr
   * @returns {boolean}
   */
  isLowerCaseStringEqual(firstStr, secondStr) {
    return firstStr && secondStr && firstStr.toLowerCase() === secondStr.toLowerCase();
  },
  isAlphanumeric(str) {
    const regex = new RegExp(`^[a-zA-Z0-9]*$`);
    if (!regex.test(str)) {
      return false;
    }
    return true;
  },
  isWithinCharacterRange(str, min, max) {
    return str.length >= min && str.length <= max;
  },
};

const ALPHABETS = [
  "A",
  "B",
  "C",
  "D",
  "E",
  "F",
  "G",
  "H",
  "I",
  "J",
  "K",
  "L",
  "M",
  "N",
  "O",
  "P",
  "Q",
  "R",
  "S",
  "T",
  "U",
  "V",
  "W",
  "X",
  "Y",
  "Z",
];

export default StringUtils;
